import { environment } from '../../environments/environment';

/**
 * Session cookie that opts a browser out of the Phase 4 New Zing redirect, so
 * automated regression suites can drive classic Zing top-level instead of being
 * bounced into the New Zing host and having to reach through its iframe.
 *
 * Honored only where `environment.allowClassicExperience` is true, which is every
 * build except production. Set by the test harness before its first navigation;
 * see "Automated testing against classic Zing" in the README.
 */
export const FORCE_CLASSIC_COOKIE = 'jm_force_classic_zing';

function hasForceClassicCookie(doc: Document): boolean {
  try {
    // Exact match on the whole entry so a cookie whose name merely ends in ours
    // (e.g. not_jm_force_classic_zing=1) cannot trigger the override.
    return doc.cookie
      .split(';')
      .some(entry => entry.trim() === `${FORCE_CLASSIC_COOKIE}=1`);
  } catch {
    // document.cookie can throw when cookie access is blocked outright.
    return false;
  }
}

// Whether classic Zing should hand off to New Zing. Phase 4 sends everyone to New
// Zing, including local dev, so this returns true unless something explicitly keeps
// us on the legacy app.
export function shouldRedirectToNewZing(doc: Document): boolean {
  // No New Zing target configured (uat, training): redirecting to '' resolves against
  // the current origin and bounces the route back here forever, so stay on classic.
  if (!environment.betaExperienceUrl) {
    return false;
  }

  // Automation escape hatch. Compile-time gated: the production environment sets
  // allowClassicExperience to false, so this branch cannot be reached in prod.
  if (environment.allowClassicExperience && hasForceClassicCookie(doc)) {
    return false;
  }

  return true;
}

// Documents that have already started navigating to New Zing.
const handoffStarted = new WeakSet<Document>();

// Sends the browser to New Zing, at most once per page load. After a login the callback
// hands off with the deep link, but the OIDC client still navigates to postLoginRoute,
// whose opt-in guard would otherwise assign location.href again and cancel the in-flight
// deep-linked request, landing the user on the dashboard instead.
export function redirectToNewZing(doc: Document, path: string): void {
  if (handoffStarted.has(doc)) {
    return;
  }
  handoffStarted.add(doc);
  doc.location.href = environment.betaExperienceUrl + path;
}
